/**
 * Shopify Cart Progress Manager
 * Dynamically recalculates tier progress and updates the DOM whenever the cart changes.
 */
(() => {
  'use strict';

  // Prevent duplicate script initialization on dynamic theme re-renders
  if (window.__CartProgressManagerInitialized) {
    if (typeof window.updateCartProgress === 'function') {
      window.updateCartProgress();
    }
    return;
  }
  window.__CartProgressManagerInitialized = true;

  // Safely inject Remix Icon CSS if not present
  try {
    if (!document.querySelector('link[href*="remixicon"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css';
      document.head.appendChild(link);
    }
  } catch (e) {
    // Silent fail if head mutation is blocked
  }

  let isFetching = false;
  let lastCartState = null;

  /**
   * Updates only the DOM elements for all active cart progress widgets.
   * @param {Object} cart - Shopify Cart response object from /cart.js
   */
  function renderProgress(cart) {
    const widgets = document.querySelectorAll('[data-cart-progress-widget]');
    if (!widgets.length) return;

    // Cache latest cart state for DOM replacement observers
    lastCartState = cart;

    const itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

    // Capped non-repeatable progress calculation (Max 4)
    const tierItems = Math.min(itemCount, 4);
    const percent = tierItems * 25;
    const isUnlocked = tierItems === 4;

    widgets.forEach((widget) => {
      const counter = widget.querySelector('[data-cart-progress-counter]');
      const fill = widget.querySelector('[data-cart-progress-fill]');
      const icon = widget.querySelector('[data-cart-progress-icon]');
      const title = widget.querySelector('[data-cart-progress-title]');
      const subtitle = widget.querySelector('[data-cart-progress-subtitle]');
      const msg = widget.querySelector('[data-cart-progress-msg]');
      const upsell = widget.querySelector('[data-cart-progress-upsell]');
      const track = widget.querySelector('.cart-progress__track');

      // 1. Counter update
      if (counter) counter.textContent = `${tierItems} / 4 Products`;

      // 2. ARIA update
      if (track) track.setAttribute('aria-valuenow', percent);

      // 3. Progress Fill width & status class
      if (fill) {
        fill.style.width = `${percent}%`;
        fill.classList.toggle('is-success', isUnlocked);
      }

      // 4. Icon update
      if (icon) {
        icon.className = isUnlocked
          ? 'cart-progress__icon ri-checkbox-circle-fill is-success'
          : 'cart-progress__icon ri-gift-line';
      }

      // 5. Title & Subtitle
      if (title) title.textContent = isUnlocked ? 'FREE Gift Unlocked' : 'Buy 3 Get 1 FREE';
      if (subtitle) subtitle.textContent = 'Add 4 products and pay for only 3.';

      // 6. Dynamic Message text
      if (msg) {
        if (tierItems === 0) {
          msg.textContent = 'Start adding products to unlock your FREE jewellery.';
        } else if (tierItems === 1) {
          msg.textContent = 'Great start! Add 3 more products.';
        } else if (tierItems === 2) {
          msg.textContent = 'Halfway there! Only 2 more to go.';
        } else if (tierItems === 3) {
          msg.textContent = "You're almost there! Just 1 more product unlocks your FREE gift.";
        } else if (tierItems === 4) {
          msg.textContent = 'Congratulations! Your FREE jewellery has been unlocked. Discount will be applied automatically.';
        }
      }

      // 7. Upsell visibility toggle (Triggers strictly at 3/4 products)
      if (upsell) {
        upsell.classList.toggle('is-hidden', tierItems !== 3);
      }
    });
  }

  /**
   * Fetches latest cart object via /cart.js, calculates tier state, and renders changes.
   * Lock mechanism prevents race conditions/duplicate overlapping requests.
   */
  async function updateCartProgress() {
    if (isFetching) return;
    isFetching = true;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Cart fetch HTTP error: ${response.status}`);
      }
      const cart = await response.json();
      renderProgress(cart);
    } catch (error) {
      console.error('[CartProgress] Failed to refresh cart data:', error);
    } finally {
      isFetching = false;
    }
  }

  // Expose globally for manual theme trigger compatibility
  window.updateCartProgress = updateCartProgress;
  window.renderProgress = renderProgress;

  // Helper matcher for Shopify cart REST mutation routes
  const isCartMutationUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
  };

  // Intercept native fetch API requests
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const requestUrl = typeof args[0] === 'string'
        ? args[0]
        : (args[0] && args[0].url ? args[0].url : '');

      if (isCartMutationUrl(requestUrl)) {
        setTimeout(updateCartProgress, 100);
      }
    } catch (e) {
      // Ignore interception parse errors
    }
    return response;
  };

  // Intercept XMLHttpRequest (XHR) API requests
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._cartProgressUrl = url;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    this.addEventListener('load', () => {
      if (isCartMutationUrl(this._cartProgressUrl)) {
        setTimeout(updateCartProgress, 100);
      }
    });
    return originalXHRSend.apply(this, arguments);
  };

  // Listen for common Theme and Shopify Cart Custom Events
  const cartEvents = [
    'cart:updated',
    'cart:change',
    'cart:refresh',
    'theme:cart:update',
    'cart:add',
    'page:loaded',
    'shopify:section:load'
  ];

  cartEvents.forEach((eventName) => {
    document.addEventListener(eventName, updateCartProgress);
  });

  // Handle Browser Navigation (bfcache / back & forward buttons)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      updateCartProgress();
    }
  });

  // Handle Tab Focus / User returning to the tab
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateCartProgress();
    }
  });

  // Observe Dynamic HTML Replacement
  const observer = new MutationObserver((mutations) => {
    let widgetInserted = false;

    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches('[data-cart-progress-widget]') || node.querySelector('[data-cart-progress-widget]')) {
              widgetInserted = true;
              break;
            }
          }
        }
      }
      if (widgetInserted) break;
    }

    if (widgetInserted) {
      if (lastCartState) {
        renderProgress(lastCartState);
      } else {
        updateCartProgress();
      }
    }
  });

  const startObserver = () => {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      startObserver();
      updateCartProgress();
    });
  } else {
    startObserver();
    updateCartProgress();
  }
})();